import { useEffect, useState } from 'react';

// Components
import DataTable from '../../components/DataTable';
import AddEditJob from './AddEditJob';
import { ActionButtonForJob, Switch } from '../../components/ActionButtons';

// Constants
import { JOBS_COLUMNS } from '../../constants/tableColumns';
import {
  ACTION_TYPE,
  DEBOUNCE_TIMEOUT,
  INITIAL_PAGE_NUMBER,
  INITIAL_SIZE,
  JobModes,
  TABLE_NAME
} from '../../constants/common';
import { debounce } from '../../utils/debounce';
import { isDarkMode } from '../../utils/common';

// Redux
import { deleteJob, getJobById, getJobsList, jobStatus } from '../../redux/actions/jobs';
import { getInterviewTechnology } from '../../redux/actions/hr';

// Utility Packages
import { useDispatch, useSelector } from 'react-redux';
import { confirmDialog } from 'primereact/confirmdialog';
import ViewJobDescription from '../../components/popup/ViewJobDescription';
import JobHrTimelineModal from '../../components/popup/JobHrTimelineModal';
import JobHrAllocationModal from '../../components/popup/JobHrAllocationModal';
import HrAvatarGroup from '../../components/HrAvatarGroup';
import { Chip } from '@mui/material';

const jobsTableStates = {
  searchValue: '',
  pageNumber: INITIAL_PAGE_NUMBER,
  size: INITIAL_SIZE,
  sortBy: 'DateModified',
  orderBy: 'desc',
  technologies: ''
};

const Jobs = () => {
  const dispatch = useDispatch();
  const jobs = useSelector((state) => state.jobs);
  const interviewTechnologyType = useSelector((state) => state.hrs.interviewTechnologyType);

  const [tableStates, setTableStates] = useState(jobsTableStates);
  const [jobsData, setJobsData] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [addEditJobModal, setAddEditJobModal] = useState(false);
  const [visibleJobDescription, setVisibleJobDescription] = useState(false);
  const [jobDescription, setJobDescription] = useState(null);

  // HR Timeline Modal states
  const [visibleHrTimeline, setVisibleHrTimeline] = useState(false);
  const [timelineJob, setTimelineJob] = useState(null);

  // HR Allocation Modal states
  const [visibleHrAllocation, setVisibleHrAllocation] = useState(false);
  const [allocationJob, setAllocationJob] = useState(null);

  useEffect(() => {
    dispatch(getInterviewTechnology());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getJobsList(tableStates));
  }, [tableStates, jobs?.isDeleted, jobs?.isUpdated, jobs?.isAdded]);

  useEffect(() => {
    if (allocationJob && jobs?.jobs?.length > 0) {
      const updatedJob = jobs.jobs.find(
        (j) => (j.ID || j.id) === (allocationJob.ID || allocationJob.id)
      );
      if (updatedJob) {
        setAllocationJob(updatedJob);
      }
    }
  }, [jobs?.jobs]);

  const handleRowActions = (actionType, rowId, jobDescription, jobObj) => {
    if (actionType === ACTION_TYPE.EDIT) {
      setSelectedJob(rowId);
      dispatch(getJobById(rowId));
      setAddEditJobModal(true);
    } else if (actionType === ACTION_TYPE.DELETE) {
      confirmDialog({
        header: 'Delete',
        message: 'Are you sure you want to delete?',
        icon: 'pi pi-exclamation-triangle',
        accept: () => dispatch(deleteJob(rowId))
      });
    } else if (actionType === ACTION_TYPE.DESCRIPTION) {
      setVisibleJobDescription(true);
      setJobDescription(jobDescription);
    } else if (actionType === ACTION_TYPE.HR_TIMELINE) {
      setTimelineJob(jobObj);
      setVisibleHrTimeline(true);
    } else if (actionType === ACTION_TYPE.ASSIGN_HR) {
      setAllocationJob(jobObj);
      setVisibleHrAllocation(true);
    }
  };

  const handleActive = (event, id) => {
    const newStatus = event.value;
    if (!newStatus || newStatus === 2 || newStatus === false) {
      confirmDialog({
        header: 'Inactivate Job',
        message:
          'Are you sure you want to inactivate this job and relieve assigned HRs from this job?',
        icon: 'pi pi-exclamation-triangle',
        accept: () => dispatch(jobStatus({ id, Status: 2 }))
      });
    } else {
      confirmDialog({
        header: 'Activate Job',
        message: 'Are you sure you want to activate this job?',
        icon: 'pi pi-exclamation-triangle',
        accept: () => dispatch(jobStatus({ id, Status: 1 }))
      });
    }
  };

  useEffect(() => {
    const modifiedJobs = jobs?.jobs?.map((job, index) => {
      return {
        ...job,
        Experience:
          job.MaxExperience !== undefined &&
          job.MaxExperience !== null &&
          Number(job.MaxExperience) !== Number(job.Experience)
            ? `${job.Experience} - ${job.MaxExperience} Y`
            : `${job.Experience} Y`,
        JobMode: job.JobMode ? JobModes.find((op) => op.code === job.JobMode)?.name : '',
        AssignedHRs: <HrAvatarGroup assignedHRs={job.assignedHRs} />,
        Technology: (
          <ul className="technology-list" key={index}>
            {job?.technologies && job?.technologies.length > 0
              ? job?.technologies?.map((tech, innerIndex) => (
                  <li
                    key={tech?.ID || tech?.id || innerIndex}
                    style={{
                      backgroundColor: `${
                        isDarkMode()
                          ? tech?.DarkModeColor || tech?.darkModeColorCode
                          : tech?.TechnologyColor || tech?.colorCode
                      }`
                    }}
                    className="technology-badge">
                    {tech.Technology || tech.name}
                  </li>
                ))
              : '--'}
          </ul>
        ),
        Skills: (
          <ul className="technology-list" key={index}>
            {job?.Skills && job?.Skills.length > 0
              ? job?.Skills?.map((tech, innerIndex) => (
                  <Chip key={innerIndex} label={tech} className="single-param mr-2" />
                ))
              : '--'}
          </ul>
        ),
        action: (
          <div className="flex align-items-center">
            <Switch UserStatus={job.Status} onChange={(e) => handleActive(e, job.ID)} />
            <ActionButtonForJob
              onClick={(actionType) =>
                handleRowActions(actionType, job?.ID, job?.JobDescription, job)
              }
            />
          </div>
        )
      };
    });
    setJobsData(modifiedJobs);
  }, [jobs, isDarkMode()]);

  const handlePagination = (pageNumber, size) =>
    setTableStates((prevState) => ({
      ...prevState,
      pageNumber,
      size
    }));

  const handleSearch = (searchValue) => {
    setTableStates((prevState) => ({
      ...prevState,
      searchValue,
      pageNumber: INITIAL_PAGE_NUMBER
    }));
  };

  const handleTechnologyFilter = (techVal) => {
    setTableStates((prevState) => ({
      ...prevState,
      technologies: Array.isArray(techVal) ? techVal.join(',') : techVal || '',
      pageNumber: INITIAL_PAGE_NUMBER
    }));
  };

  const handleClearFilter = () => {
    setTableStates(jobsTableStates);
  };

  const handleSort = (sortBy) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        sortBy,
        orderBy: prevState.orderBy === 'asc' ? 'desc' : 'asc'
      };
    });
  };

  return (
    <>
      <DataTable
        name={TABLE_NAME.JOBS}
        searchPlaceHolder="Search by Job Title"
        totalRecords={jobs?.totalJobs || jobs?.pagination?.totalRecords}
        onPagination={handlePagination}
        pageNumber={tableStates.pageNumber}
        pageSize={tableStates.size}
        onSearch={debounce(handleSearch, DEBOUNCE_TIMEOUT)}
        onSort={handleSort}
        onAdd={() => {
          setSelectedJob(null);
          setAddEditJobModal(true);
        }}
        data={jobsData}
        columns={JOBS_COLUMNS}
        isDropdownMultiSelect
        dropdownPlaceHolder="Select Technology"
        onStatusFilter={handleTechnologyFilter}
        dropdown={
          interviewTechnologyType?.length
            ? interviewTechnologyType.map((tech) => ({
                name: tech.Technology,
                value: tech.ID
              }))
            : []
        }
        dropdownValue={
          tableStates.technologies
            ? typeof tableStates.technologies === 'string'
              ? tableStates.technologies.split(',').filter(Boolean)
              : tableStates.technologies
            : []
        }
        clearFilter
        isClearFilterDisabled={
          tableStates.searchValue === '' &&
          (!tableStates.technologies || tableStates.technologies.length === 0)
        }
        clearFilterOnClick={handleClearFilter}
      />

      {addEditJobModal && (
        <AddEditJob
          show={addEditJobModal}
          ID={selectedJob}
          onHide={() => {
            setSelectedJob(null);
            setAddEditJobModal(false);
          }}
        />
      )}

      <ViewJobDescription
        header="Job Description"
        visible={visibleJobDescription}
        onHide={() => setVisibleJobDescription(false)}
        description={jobDescription}
      />

      {visibleHrTimeline && (
        <JobHrTimelineModal
          visible={visibleHrTimeline}
          onHide={() => {
            setVisibleHrTimeline(false);
            setTimelineJob(null);
          }}
          job={timelineJob}
        />
      )}

      {visibleHrAllocation && (
        <JobHrAllocationModal
          visible={visibleHrAllocation}
          onHide={() => {
            setVisibleHrAllocation(false);
            setAllocationJob(null);
          }}
          job={allocationJob}
          onRefresh={() => dispatch(getJobsList(tableStates))}
        />
      )}
    </>
  );
};

export default Jobs;
